import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
} from '@mui/icons-material';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { changePassword } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('changePassword.mismatch'));
      return;
    }
    if (formData.newPassword.length < 6) {
      setError(t('changePassword.tooShort'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const result = await changePassword(formData.currentPassword, formData.newPassword);

    if (result.success) {
      setSuccess(t('changePassword.success'));
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => navigate('/'), 2000);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const passwordField = (name, labelKey, field) => (
    <TextField
      fullWidth
      label={t(labelKey)}
      name={name}
      type={showPasswords[field] ? 'text' : 'password'}
      value={formData[name]}
      onChange={handleChange}
      required
      margin="normal"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LockIcon color="action" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => togglePasswordVisibility(field)} edge="end">
              {showPasswords[field] ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        {t('changePassword.title')}
      </Typography>

      <Card sx={{ maxWidth: 500 }}>
        <CardContent sx={{ p: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            {passwordField('currentPassword', 'changePassword.current', 'current')}
            {passwordField('newPassword', 'changePassword.new', 'new')}
            {passwordField('confirmPassword', 'changePassword.confirm', 'confirm')}

            <Box display="flex" gap={2} mt={3}>
              <Button variant="outlined" onClick={() => navigate('/')} fullWidth>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" fullWidth disabled={loading}>
                {loading ? t('changePassword.changing') : t('changePassword.submit')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePassword;
